import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import {
  CurrencyIcon,
  CurrencyIconPair,
  CurrencyIconSlot,
} from "../../../../Components/Atoms/CurrencyIcon";
import {
  GRID_HISTORY_COLS_4,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
  GridHistoryStatusActionsCell,
  GridHistoryStatusActionsHeader,
  GridPagination,
} from "../../../../Components/Molecules/GridHistory";
import { StablesHistoryTypeTabs } from "../../Stables/StablesHistoryTypeTabs";
import { useContaInternacionalHistory } from "../../../../Hooks/useContaInternacionalHistory";
import {
  formatBrl,
  formatCiConversionHistoryTitle,
  formatCiPurchaseHistoryDetail,
  formatCiPurchaseHistoryTitle,
  formatCiSentAmountDetail,
  formatCiTransferHistoryTitle,
  formatCiWithdrawalHistoryAmount,
  formatCiWithdrawalHistoryFeeDetail,
  formatCiWithdrawalHistoryTitle,
  formatCurrencyAmount,
  getCiStatusLabel,
  getGeneralHistoryTabs,
  getHistoryKindMeta,
} from "../constants";
import {
  EMPTY_EXCHANGE_FILTERS,
  applyExchangeFilters,
} from "../contaInternacionalExchangeFilters";
import {
  EMPTY_CONTA_INTERNACIONAL_HISTORY_FILTERS,
  applyContaInternacionalHistoryFilters,
} from "../contaInternacionalHistoryFilters";
import { ContaInternacionalExchangeFilterBar } from "./ContaInternacionalExchangeFilterBar";
import { ContaInternacionalHistoryDetailModal } from "./ContaInternacionalHistoryDetailModal";
import { ContaInternacionalHistoryFilterBar } from "./ContaInternacionalHistoryFilterBar";

const DEFAULT_ROWS_PER_PAGE = 10;

const StatusBadge = ({ status }) => {
  const label = getCiStatusLabel(status);
  const isPending = String(status || "").toLowerCase().includes("process");
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
        isPending
          ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
          : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
      }`}
    >
      {label}
    </span>
  );
};

const TypeBadge = ({ historyKind }) => {
  const meta = getHistoryKindMeta()[historyKind];
  if (!meta) return null;

  return (
    <span
      className={`inline-flex w-[5.5rem] shrink-0 items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.badgeClass}`}
    >
      {meta.label}
    </span>
  );
};

const HistoryHeaderRow = () => (
  <GridHistoryHeader colSpan={4}>
    <span>{intl.get("grid.transaction")}</span>
    <span>{intl.get("grid.date")}</span>
    <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>{intl.get("grid.value")}</span>
    <GridHistoryStatusActionsHeader statusLabel={intl.get("grid.status")} />
  </GridHistoryHeader>
);

const getGeneralRowContent = (item) => {
  switch (item.historyKind) {
    case "purchases":
      return {
        icon: <CurrencyIcon asset={item.asset} />,
        title: formatCiPurchaseHistoryTitle(item),
        date: item.date,
        amount: formatBrl(item.amountBrl),
        detail: formatCiPurchaseHistoryDetail(item),
      };
    case "withdrawals":
      return {
        icon: <CurrencyIcon asset={item.asset} />,
        title: formatCiWithdrawalHistoryTitle(item),
        date: item.date,
        subtitle: item.destination,
        amount: formatCiWithdrawalHistoryAmount(item),
        detail: formatCiWithdrawalHistoryFeeDetail(item),
      };
    case "transfers":
      return {
        icon: <CurrencyIcon asset={item.asset} />,
        title: formatCiTransferHistoryTitle(item),
        date: item.date,
        subtitle: item.recipient,
        amount: null,
        detail: item.method,
      };
    case "conversions":
      return {
        icon: <CurrencyIconPair from={item.from} to={item.to} />,
        title: formatCiConversionHistoryTitle(item),
        date: item.date,
        amount: formatCurrencyAmount(item.amountTo, item.to),
        detail: formatCiSentAmountDetail(item),
      };
    default:
      return null;
  }
};

const GeneralHistory = ({ items, onShowDetail }) => {
  if (items.length === 0) {
    return (
      <div className="app-empty-state m-4">
        {intl.get("contaInternacional.noOperationsFiltered")}
      </div>
    );
  }

  return (
    <GridHistoryContainer gridCols={GRID_HISTORY_COLS_4}>
      <HistoryHeaderRow />
      {items.map((item) => {
        const content = getGeneralRowContent(item);
        if (!content) return null;

        return (
          <GridHistoryRow key={`${item.historyKind}-${item.id}`} colSpan={4}>
            <GridHistoryCell label={intl.get("grid.transaction")}>
              <div className="flex min-w-0 items-center gap-3">
                <CurrencyIconSlot>{content.icon}</CurrencyIconSlot>
                <TypeBadge historyKind={item.historyKind} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {content.title}
                  </p>
                  {content.subtitle && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{content.subtitle}</p>
                  )}
                </div>
              </div>
            </GridHistoryCell>
            <GridHistoryCell label={intl.get("grid.date")}>
              <p className="text-xs text-gray-500 dark:text-gray-400">{content.date}</p>
            </GridHistoryCell>
            <GridHistoryCell label={intl.get("grid.value")} align="right">
              {content.amount ? (
                <>
                  <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {content.amount}
                  </p>
                  {content.detail && (
                    <p className="text-[11px] text-gray-400">{content.detail}</p>
                  )}
                </>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </GridHistoryCell>
            <GridHistoryStatusActionsCell
              statusLabel={intl.get("grid.status")}
              statusBadge={<StatusBadge status={item.status} />}
              onShowDetail={onShowDetail}
              item={item}
            />
          </GridHistoryRow>
        );
      })}
    </GridHistoryContainer>
  );
};

const countByKind = (items, kind) =>
  items.filter((item) => item.historyKind === kind).length;

export const ContaInternacionalHistoryPanel = ({ title }) => {
  const resolvedTitle = title ?? intl.get("contaInternacional.generalHistory");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState(EMPTY_CONTA_INTERNACIONAL_HISTORY_FILTERS);
  const [exchangeFilters, setExchangeFilters] = useState(EMPTY_EXCHANGE_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const { items: allHistory, loading: historyLoading, error: historyError, reload } =
    useContaInternacionalHistory();

  const isExchangeTab = activeTab === "conversions";

  const filteredHistory = useMemo(
    () => applyContaInternacionalHistoryFilters(allHistory, filters),
    [allHistory, filters],
  );

  const filteredExchanges = useMemo(
    () =>
      applyExchangeFilters(
        allHistory.filter((item) => item.historyKind === "conversions"),
        exchangeFilters,
      ),
    [allHistory, exchangeFilters],
  );

  const tabCounts = useMemo(
    () => ({
      all: filteredHistory.length,
      purchases: countByKind(filteredHistory, "purchases"),
      withdrawals: countByKind(filteredHistory, "withdrawals"),
      transfers: countByKind(filteredHistory, "transfers"),
      conversions: isExchangeTab
        ? filteredExchanges.length
        : countByKind(filteredHistory, "conversions"),
    }),
    [filteredExchanges.length, filteredHistory, isExchangeTab],
  );

  const displayItems = useMemo(() => {
    if (isExchangeTab) return filteredExchanges;
    if (activeTab === "all") return filteredHistory;
    return filteredHistory.filter((item) => item.historyKind === activeTab);
  }, [activeTab, filteredExchanges, filteredHistory, isExchangeTab]);

  const totalItems = displayItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedItems = useMemo(() => {
    if (totalItems === 0) return [];
    const start = (page - 1) * rowsPerPage;
    return displayItems.slice(start, start + rowsPerPage);
  }, [displayItems, page, rowsPerPage, totalItems]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const handleExchangeFiltersChange = (nextFilters) => {
    setExchangeFilters(nextFilters);
    setPage(1);
  };

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    setPage(1);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  return (
    <section>
      <h2 className="mb-3 text-sm font-bold text-brand-dark dark:text-gray-100">{resolvedTitle}</h2>
      <div className="mb-4 space-y-3">
        <StablesHistoryTypeTabs
          tabs={getGeneralHistoryTabs()}
          tabsLabelKey="contaInternacional.historyTabsLabel"
          counts={tabCounts}
          activeTab={activeTab}
          onChange={handleTabChange}
        />
        {isExchangeTab ? (
          <ContaInternacionalExchangeFilterBar
            filters={exchangeFilters}
            onFiltersChange={handleExchangeFiltersChange}
            filteredItems={displayItems}
          />
        ) : (
          <ContaInternacionalHistoryFilterBar
            filters={filters}
            onFiltersChange={handleFiltersChange}
            filteredItems={displayItems}
          />
        )}
      </div>
      <div className="app-card overflow-hidden">
        {historyLoading ? (
          <div className="app-empty-state m-4">
            {intl.get("common.loading") || "Carregando…"}
          </div>
        ) : historyError ? (
          <div className="m-4 space-y-3">
            <p className="text-sm text-gray-500">
              {intl.get("contaInternacional.balancesLoadError")}
            </p>
            <button type="button" className="btn-outline" onClick={reload}>
              {intl.get("contaInternacional.retryLoad")}
            </button>
          </div>
        ) : (
          <>
            <GeneralHistory items={paginatedItems} onShowDetail={setSelectedItem} />
            <GridPagination
              total={totalItems}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              itemLabel={intl.get("contaInternacional.operationsItemLabel")}
            />
          </>
        )}
      </div>

      <ContaInternacionalHistoryDetailModal
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
      />
    </section>
  );
};
